import type { DataUpdateGroupId } from './dataUpdateGroups';

export type DpTimestamp = { toDate(): Date };

export const DP_REQUEST_TYPES = ['data_update', 'ticket', 'day_off', 'overtime', 'raro_invest', 'health_plan'] as const;
export type DpRequestType = (typeof DP_REQUEST_TYPES)[number];

export const DATA_UPDATE_STATUSES = ['pending_dp', 'pending_correction', 'approved', 'rejected', 'cancelled'] as const;
export type DataUpdateStatus = (typeof DATA_UPDATE_STATUSES)[number];

export const TICKET_STATUSES = ['open', 'waiting_collaborator', 'resolved', 'cancelled'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const DAY_OFF_STATUSES = ['pending_manager', 'approved', 'rejected', 'cancelled', 'expired'] as const;
export type DayOffStatus = (typeof DAY_OFF_STATUSES)[number];

export const OVERTIME_STATUSES = ['pending_dp', 'registered', 'rejected', 'cancelled'] as const;
export type OvertimeStatus = (typeof OVERTIME_STATUSES)[number];

export const RARO_INVEST_STATUSES = ['pending_dp', 'approved', 'reimbursed', 'rejected', 'cancelled'] as const;
export type RaroInvestStatus = (typeof RARO_INVEST_STATUSES)[number];

export const HEALTH_PLAN_STATUSES = ['pending_dp', 'approved', 'rejected', 'cancelled'] as const;
export type HealthPlanStatus = (typeof HEALTH_PLAN_STATUSES)[number];

export type DpRequestStatus =
    | DataUpdateStatus
    | TicketStatus
    | DayOffStatus
    | OvertimeStatus
    | RaroInvestStatus
    | HealthPlanStatus;

export const OVERTIME_COMPENSATION_TYPES = ['compensation', 'payment'] as const;
export type OvertimeCompensationType = (typeof OVERTIME_COMPENSATION_TYPES)[number];

export const HEALTH_PLAN_ACTIONS = ['include', 'change', 'leave'] as const;
export type HealthPlanAction = (typeof HEALTH_PLAN_ACTIONS)[number];

export const HEALTH_PLAN_PROVIDERS = ['Bradesco', 'Unimed', 'Noterdame', 'Outro'] as const;
export type HealthPlanProvider = (typeof HEALTH_PLAN_PROVIDERS)[number];

export const CLIENT_APPROVAL_STATUSES = ['pending', 'approved', 'rejected', 'not_required'] as const;
export type ClientApprovalStatus = (typeof CLIENT_APPROVAL_STATUSES)[number];

export const TICKET_CATEGORIES = ['reserved', 'other'] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];

export const DAY_OFF_PERIODS = ['full', 'morning', 'afternoon'] as const;
export type DayOffPeriod = (typeof DAY_OFF_PERIODS)[number];

export interface DpDecision {
    by: string;
    at: DpTimestamp;
    note: string | null;
}

export const DP_ATTACHMENT_KINDS = ['raro_manager', 'client_manager'] as const;
export type DpAttachmentKind = (typeof DP_ATTACHMENT_KINDS)[number];

export interface DpAttachment {
    kind?: DpAttachmentKind;
    name: string;
    url: string;
    path: string;
    contentType: string;
    size: number;
}

export interface DataUpdateChange {
    group: string;
    field: string;
    label: string;
    before: unknown;
    after: unknown;
}

export interface DpRequestBase {
    id: string;
    type: DpRequestType;
    status: DpRequestStatus;
    collaboratorId: string;
    collaboratorEmail: string;
    collaboratorName: string;
    createdAt: DpTimestamp;
    updatedAt: DpTimestamp;
    statusChangedAt: DpTimestamp;
    statusChangedBy: string;
}

export interface DataUpdateRequest extends DpRequestBase {
    type: 'data_update';
    status: DataUpdateStatus;
    groups: DataUpdateGroupId[];
    changes: DataUpdateChange[];
    attachments: DpAttachment[];
    decision: DpDecision | null;
}

export interface TicketRequest extends DpRequestBase {
    type: 'ticket';
    status: TicketStatus;
    category: TicketCategory;
    subject: string;
    attachments?: DpAttachment[];
    lastMessageAt: DpTimestamp;
    lastMessageBy: 'collaborator' | 'dp';
}

export interface DayOffDay {
    date: string;
    period: DayOffPeriod;
}

export interface DayOffRequest extends DpRequestBase {
    type: 'day_off';
    status: DayOffStatus;
    days: DayOffDay[];
    totalDays: number;
    reason: string | null;
    managerEmail: string;
    managerDecision: DpDecision | null;
    dpRegistration: { by: string; at: DpTimestamp } | null;
    kind?: 'birthday';
    clientEmails?: string[];
    clientApproval?: ClientApproval;
}

export interface ClientApproval {
    status: ClientApprovalStatus;
    by: string | null;
    at: DpTimestamp | null;
    note: string | null;
}

export interface OvertimeRequest extends DpRequestBase {
    type: 'overtime';
    status: OvertimeStatus;
    contractRegime: string;
    projectName: string;
    internalProject?: boolean;
    clientManagerEmails: string[];
    raroManagerEmail: string;
    date: string;
    startTime: string;
    endTime: string;
    durationMinutes: number;
    reason: string;
    compensationType: OvertimeCompensationType;
    attachments: DpAttachment[];
    decision: DpDecision | null;
}

export interface RaroInvestRequest extends DpRequestBase {
    type: 'raro_invest';
    status: RaroInvestStatus;
    referenceMonth: string;
    amount: number;
    note: string | null;
    attachments: DpAttachment[];
    decision: DpDecision | null;
    reimbursement: { by: string; at: DpTimestamp } | null;
}

export interface HealthPlanDependentState {
    name: string;
    relationship: string;
    isCovered: boolean;
}

export interface HealthPlanSnapshot {
    hasHealthInsurance: boolean;
    provider: string | null;
    inclusionDate: string | null;
    dependents: HealthPlanDependentState[];
}

export interface HealthPlanRequest extends DpRequestBase {
    type: 'health_plan';
    status: HealthPlanStatus;
    action: HealthPlanAction;
    provider: HealthPlanProvider | null;
    includeHolder: boolean;
    dependentNames: string[];
    note: string | null;
    attachments: DpAttachment[];
    before: HealthPlanSnapshot;
    decision: (DpDecision & { effectiveDate: string | null }) | null;
}

export type DpRequest =
    | DataUpdateRequest
    | TicketRequest
    | DayOffRequest
    | OvertimeRequest
    | RaroInvestRequest
    | HealthPlanRequest;

export interface DpRequestMessage {
    id: string;
    authorEmail: string;
    authorSide: 'collaborator' | 'dp';
    body: string;
    attachments: DpAttachment[];
    createdAt: DpTimestamp;
}
